import {useEffect} from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { TvPlatform } from "./Models/Enums/TvPlatforms";
import { RoutePaths } from './Constants/RoutePaths';

// Style
import "./SCSS/App.scss";

// Root and General Use Screens
import RootLayout from "./Screens/RootLayout";
import ErrorScreen from "./Screens/ErrorScreen";

// Landing Page Screens
import FrontPage from "./Screens/LandingPages/FrontPage";
import PreSignUp from "./Screens/LandingPages/PreSignUp";
import LifetimeMembership from "./Screens/LandingPages/LifetimeMembership";

// Watch Screens
import WatchVideo from "./Screens/Content/Watch/VideoSingle/WatchVideo";
import TrailerFilm from "./Screens/Content/Watch/VideoSingle/Trailer/TrailerFilm";
import TrailerSeries from "./Screens/Content/Watch/VideoSingle/Trailer/TrailerSeries";

// David Icke Screens
import DavidIckeRoot from "./Screens/DavidIcke/DavidIckeRoot";
import DavidIckeFilms from "./Screens/DavidIcke/DavidIckeFilms";
import DavidIckeLive from "./Screens/DavidIcke/DavidIckeLive";
import DavidIckeSeries from "./Screens/DavidIcke/DavidIckeSeries";

// Content List Screens
import Browse from "./Screens/Content/Watch/Browse";
import FilmsFeed from "./Screens/Content/Watch/Films/FilmsFeed";
import SeriesFeed from "./Screens/Content/Watch/Series/SeriesFeed";
import WeeklyShows from "./Screens/Content/Watch/WeeklyShows";
import SeriesSingle from "./Screens/Content/Watch/Series/SeriesSingle";
import CategoryScreen from "./Screens/Content/Watch/Categories/CategoryScreen";

// Read Screens
import ArticlesFeed from "./Screens/Content/Read/ArticlesFeed";
import ArticleCategoryScreen from "./Screens/Content/Read/ArticleCategoryScreen";
import ArticleSingle from "./Screens/Content/Read/ArticleSingle";

// Auth / Account Screens
import LoginScreen from "./Screens/Account/Authentication/LoginScreen";
import Account from "./Screens/Account/Account";
import SignUp from "./Screens/Account/SignUp";
import SignUpSuccess from "./Screens/Account/SignUpSuccess";
import ChoosePlan from "./Screens/Account/Subscription/ChoosePlan";
import TvAuthenticationScreen from "./Screens/Account/Authentication/TvAuthenticationScreen";
import ForgotPasswordScreen, { Loader as forgotLoader } from "./Screens/Account/Password/ForgotPasswordScreen";
import ResetPasswordScreen, { Loader as resetLoader } from "./Screens/Account/Password/ResetPasswordScreen";

// Help Center Screens
import HelpCentreScreen from './Screens/Company/HelpCentreScreen';
import TermsOfUseScreen from "./Screens/Company/TermsOfUseScreen";
import PrivacyPolicyScreen from "./Screens/Company/PrivacyPolicyScreen";
import CareerScreen from "./Screens/Company/CareerScreen";

// Affiliate Screens
import AffiliateScreen, { Loader as affiliateLoader } from "./Screens/AffiliateScreen";

// Redirect Screens
import AuthScreenRedirect from "./Components/AuthScreenRedirect";
import ProtectedRoute from "../src/Components/ProtectedRoute";
import ModeratorRoute from "./Components/ModeratorRoute";

// Reporting
import ReportingMenu from "./Screens/Reporting/ReportingMenu";
import CommentModeration from "./Screens/Reporting/CommentModeration";
import DeviceCodeAuth from "./Screens/Account/Authentication/DeviceCodeAuth";
import FirestickDeviceCodeAuth from "./Screens/Account/Authentication/FirestickDeviceCodeAuth";
import {getPromotionCode} from "./Helpers/PromotionCode";
import {getAffiliateReferral, loadPromoteKitScript} from "./Helpers/AffiliateReferral";

function App() {
    // Capture an app promotion code on every entry route, not only pricing pages.
    // This preserves targeted links through login, password recovery and signup
    // navigation within the same browser tab.
    useEffect(() => {
        getPromotionCode();
        getAffiliateReferral();
        loadPromoteKitScript();
    }, []);

    const router = createBrowserRouter([{
        path: RoutePaths.Root,
        id: "root",
        element: <RootLayout />,
        errorElement: <ErrorScreen />,
        children: [
            // Unauthenticated pages
            { index: true, element: <FrontPage /> },
            { path: "Login/:redirectTo?", element: <LoginScreen /> }, // Optional parameter, do not change to RoutePaths
            { path: RoutePaths.ForgotPassword, element: <ForgotPasswordScreen />, loader: forgotLoader },
            { path: RoutePaths.ResetPassword(":userId", ":code"), element: <ResetPasswordScreen />, loader: resetLoader },
            ...RoutePaths.SignUp.map((path, index) => ({
                path,
                element: <SignUp key={index} />
            })),
            { path: RoutePaths.SignUpSuccess, element: <SignUpSuccess /> },
            { path: RoutePaths.Affiliate(":link"), element: <AffiliateScreen />, loader: affiliateLoader },

            // Create your account
            { path: RoutePaths.PreSignUp, element: <PreSignUp /> },

            // Marketing Landing Pages
            { path: RoutePaths.LifetimeMembership, element: <LifetimeMembership /> },

            // Authenticated pages
            { path: RoutePaths.Account, element: <Account /> },
            { path: RoutePaths.ChoosePlan, element: <AuthScreenRedirect FreeScreen={<SignUp />} AuthenticatedScreen={<ChoosePlan />} /> },
            { path: RoutePaths.Renew, element: <AuthScreenRedirect FreeScreen={<SignUp />} AuthenticatedScreen={<ChoosePlan />} /> },
            { path: RoutePaths.Browse, element: <Browse /> },
            { path: RoutePaths.Watch(":id"), element: <WatchVideo /> },
            { path: RoutePaths.Series, element: <SeriesFeed /> },
            { path: RoutePaths.Films, element: <FilmsFeed /> },
            { path: RoutePaths.Category(':id'), element: <CategoryScreen /> },

            // Protect the Reports route using my ProtectedRoute component
            {
                path: RoutePaths.Reports,
                element: (
                    <ProtectedRoute>
                        <ReportingMenu />
                    </ProtectedRoute>
                )
            },
            {
                path: RoutePaths.CommentModeration,
                element: (
                    <ModeratorRoute>
                        <CommentModeration />
                    </ModeratorRoute>
                )
            },

            // Both authenticated and unauthenticated
            { path: RoutePaths.BrowseArticles, element: <ArticlesFeed /> },
            { path: RoutePaths.ArticleCategory(":id"), element: <ArticleCategoryScreen /> },
            { path: RoutePaths.ReadArticle(":id"), element: <ArticleSingle /> },
            { path: RoutePaths.ReadArticleOld(":id"), element: <ArticleSingle /> },
            { path: RoutePaths.SeriesTrailer(":id"), element: <TrailerSeries /> },
            { path: RoutePaths.WatchTrailer(":id"), element: <TrailerFilm /> },
            { path: RoutePaths.SeriesEpisodes(":id"), element: <SeriesSingle /> },
            ...RoutePaths.WeeklyShows.map((path, index) => ({
                path,
                element: <WeeklyShows />
            })),
            { path: RoutePaths.HelpCentre, element: <HelpCentreScreen /> },
            { path: RoutePaths.TermsOfUse, element: <TermsOfUseScreen /> },
            { path: RoutePaths.PrivacyPolicy, element: <PrivacyPolicyScreen /> },
            { path: RoutePaths.Careers, element: <CareerScreen /> },

            // TV Auth
            { path: RoutePaths.Activate, element: <DeviceCodeAuth /> },
            // Compatibility URLs: v1 confirmation first, with a narrowly scoped
            // /rdv fallback for codes generated by genuinely old TV clients.
            { path: RoutePaths.GenericTvAuth, element: <TvAuthenticationScreen platform={TvPlatform.Generic} /> },
            { path: RoutePaths.Roku, element: <TvAuthenticationScreen platform={TvPlatform.Roku} /> },
            { path: RoutePaths.AndroidTV, element: <TvAuthenticationScreen platform={TvPlatform.AndroidTv} /> },
            { path: RoutePaths.SamsungTV, element: <TvAuthenticationScreen platform={TvPlatform.SamsungTv} /> },
            { path: RoutePaths.Firestick, element: <FirestickDeviceCodeAuth /> },

            // Additional Routes redirecting to specific pages
            { path: RoutePaths.Wtaf, element: <Navigate to={RoutePaths.SeriesEpisodes(107)} /> },
            { path: RoutePaths.DotConnector, element: <Navigate to={RoutePaths.SeriesEpisodes(121)} /> },
            { path: RoutePaths.TheDotConnector, element: <Navigate to={RoutePaths.SeriesEpisodes(121)} /> },
            { path: RoutePaths.RightNow, element: <Navigate to={RoutePaths.SeriesEpisodes(111)} /> },
            { path: RoutePaths.IsNowTheTime, element: <Navigate to={RoutePaths.SeriesEpisodes(128)} /> },
            { path: RoutePaths.DavidIcke, element: <DavidIckeRoot />},
            { path: RoutePaths.DavidIckeFilms, element: <DavidIckeFilms /> },
            { path: RoutePaths.DavidIckeLive, element: <DavidIckeLive /> },
            { path: RoutePaths.DavidIckeSeries, element: <DavidIckeSeries /> }
        ]
    }]);

    return (<RouterProvider router={router} />);
}

export default App;
